import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { readJson } from './utils/api';

const AuthContext = createContext(null);
const API_BASE = process.env.REACT_APP_API_BASE || '';

export function AuthProvider({ children }) {
  const [farmer, setFarmer] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) { setLoading(false); return; }
    fetch(`${API_BASE}/api/v1/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
      signal: AbortSignal.timeout ? AbortSignal.timeout(5000) : undefined,
    })
      .then(r => { if (!r.ok) throw new Error(); return readJson(r); })
      .then(data => setFarmer(data))
      .catch(() => { localStorage.removeItem('token'); })
      .finally(() => setLoading(false));
  }, []);

  const authFetch = useCallback((url, options = {}) => {
    const token = localStorage.getItem('token');
    const headers = options.headers || {};
    if (token && !headers.Authorization) {
      headers.Authorization = `Bearer ${token}`;
    }
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000);
    return fetch(`${API_BASE}${url}`, {
      ...options,
      headers,
      signal: options.signal || controller.signal,
    }).then(res => {
      clearTimeout(timeoutId);
      if (res.status === 401) {
        localStorage.removeItem('token');
        setFarmer(null);
        navigate('/login');
        throw new Error('Session expired');
      }
      return res;
    }).catch(err => {
      clearTimeout(timeoutId);
      throw err;
    });
  }, [navigate]);

  const login = (token, farmerData) => {
    localStorage.setItem('token', token);
    setFarmer(farmerData);
  };

  const logout = () => {
    localStorage.removeItem('token');
    setFarmer(null);
    navigate('/');
  };

  return (
    <AuthContext.Provider value={{ farmer, setFarmer, loading, authFetch, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
